import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, RotateCw, Send, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PHOTO_MIME, VIDEO_MIME, mimeOf, uploadMedia } from "@/lib/connect";

const MB = 1024 * 1024;

function checkFile(f, limits) {
  const m = mimeOf(f);
  const photo = PHOTO_MIME.includes(m);
  const video = VIDEO_MIME.includes(m);
  if ((!photo && !video) || (photo && f.size > limits.photo * MB) || (video && f.size > limits.video * MB) || !f.size)
    return `${f.name} can't be sent. Photos up to ${limits.photo} MB (JPG, PNG, WEBP, HEIC, GIF) and videos up to ${limits.video} MB (MP4, MOV, WEBM).`;
  return null;
}

export default function Composer({ base, conv, onSent, onPending, readOnly }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const typed = useRef(0);
  const input = useRef(null);
  const limits = { photo: 25, video: 200, ...(window.__connectLimits || {}) };

  const pickFiles = (list) => {
    const arr = Array.from(list || []);
    if (files.length + arr.length > 10) return setErr("You can send up to 10 at once");
    const bad = arr.map((f) => checkFile(f, limits)).find(Boolean);
    if (bad) return setErr(bad);
    setErr("");
    setFiles((x) => [...x, ...arr.map((f) => ({ f, id: Math.random().toString(36).slice(2), url: URL.createObjectURL(f) }))]);
  };
  const typing = () => { if (Date.now() - typed.current > 3000) { typed.current = Date.now(); api.post(`${base}/conversations/${conv.id}/typing`).catch(() => {}); } };
  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (t.length > 4000) return setErr("Messages can be up to 4000 characters");
    if (!t && !files.length) return;
    const client_id = `c${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
    const pend = { client_id, text: t, files, progress: 0, failed: false };
    setText(""); setFiles([]); setErr("");
    setBusy(true);
    await sendPending(pend);
    setBusy(false);
  };
  const sendPending = async (pend) => {
    onPending({ ...pend, failed: false });
    try {
      const ids = [];
      for (let i = 0; i < pend.files.length; i += 1) {
        ids.push(await uploadMedia(base, conv.id, pend.files[i].f, (p) => onPending({ ...pend, progress: (i + p) / pend.files.length })));
      }
      const { data } = await api.post(`${base}/conversations/${conv.id}/messages`, { text: pend.text, media_ids: ids, client_id: pend.client_id });
      onSent(data, pend.client_id);
    } catch (x) {
      const st = x?.response?.status;
      const detail = x?.response?.data?.detail;
      onPending({ ...pend, failed: true, error: st === 422 || st === 403 ? (typeof detail === "string" ? detail : detail?.message || errMsg(x)) : null, retry: () => sendPending(pend) });
    }
  };
  if (conv?.read_only) return <p className="border-t border-q-line p-4 text-sm text-q-muted" data-testid="composer-dm-read-only">{conv.read_only_reason}</p>;
  if (readOnly) return <p className="border-t border-q-line p-4 text-sm text-q-muted">Support view is read-only.</p>;
  return (
    <form onSubmit={send} className="border-t border-q-line p-3" data-testid="composer">
      {!!files.length && (
        <div className="mb-2 flex gap-2 overflow-x-auto" data-testid="composer-attachments">
          {files.map((x) => (
            <div key={x.id} className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-q-raised">
              {VIDEO_MIME.includes(mimeOf(x.f)) ? <video src={x.url} className="h-full w-full object-cover" muted /> : <img src={x.url} alt="" className="h-full w-full object-cover" />}
              <button type="button" onClick={() => setFiles((f) => f.filter((y) => y.id !== x.id))} className="absolute right-0 top-0 inline-flex h-11 w-11 items-start justify-end p-1" aria-label="Remove" data-testid={`composer-remove-${x.id}`}><X className="h-4 w-4 rounded-full bg-black/60 text-white" /></button>
            </div>
          ))}
        </div>
      )}
      {err && <p className="mb-2 text-sm font-semibold" style={{ color: "rgb(var(--q-danger))" }} data-testid="composer-error">{err}</p>}
      <div className="flex items-end gap-2">
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,video/mp4,video/quicktime,video/webm,.heic,.mov" className="hidden"
          onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} data-testid="composer-file-input" />
        <button type="button" onClick={() => input.current?.click()} className="q-btn q-btn-ghost h-11 w-11 flex-shrink-0 p-0" aria-label="Add photos or videos" data-testid="composer-attach-btn"><ImagePlus className="h-5 w-5" /></button>
        <textarea rows={1} value={text} maxLength={4100} onChange={(e) => { setText(e.target.value); setErr(""); typing(); }} placeholder="Message"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="q-input max-h-40 min-h-[44px] flex-1 resize-none" data-testid="composer-input" />
        <button className="q-btn q-btn-primary h-11 w-11 flex-shrink-0 p-0" disabled={busy && !files.length && !text} aria-label="Send" data-testid="composer-send-btn"><Send className="h-5 w-5" /></button>
      </div>
      {text.length > 3800 && <p className="mt-1 text-right text-xs text-q-faint" data-testid="composer-count">{text.length}/4000</p>}
    </form>
  );
}

export function PendingBubble({ p }) {
  return (
    <div className="ml-auto max-w-[80%] text-right" data-testid={`pending-${p.client_id}`}>
      <div className="inline-block rounded-2xl rounded-br-md px-4 py-2.5 text-left text-white opacity-80" style={{ background: "rgb(var(--glow-connect))" }}>
        {p.text && <p className="whitespace-pre-wrap break-words">{p.text}</p>}
        {!!p.files.length && <p className="text-sm">{p.files.length} attachment{p.files.length > 1 ? "s" : ""}</p>}
      </div>
      {p.failed && p.error ? <p className="mt-1 text-sm font-semibold" style={{ color: "rgb(var(--q-danger))" }} data-testid={`pending-error-${p.client_id}`}>{p.error}</p> : p.failed ? (
        <button onClick={p.retry} className="mt-1 inline-flex min-h-[44px] items-center gap-1 text-sm font-bold" style={{ color: "rgb(var(--q-danger))" }} data-testid={`pending-retry-${p.client_id}`}><RotateCw className="h-4 w-4" /> Not sent. Tap to retry</button>
      ) : (
        <div className="mt-1 flex items-center justify-end gap-2 text-xs text-q-faint" data-testid={`pending-progress-${p.client_id}`}>
          <Loader2 className="h-3 w-3 animate-spin" /> {p.files.length ? `Uploading ${Math.round((p.progress || 0) * 100)}%` : "Sending…"}
          {!!p.files.length && <span className="h-1.5 w-24 overflow-hidden rounded-full bg-q-raised"><span className="block h-full transition-[width] duration-200" style={{ width: `${Math.round((p.progress || 0) * 100)}%`, background: "rgb(var(--glow-connect))" }} /></span>}
        </div>
      )}
    </div>
  );
}
